import { z } from 'zod';
import { INVOICE_TYPE } from '../cobros';

const MONEY_INPUT_REGEX = /^\d+(?:\.\d{1,2})?$/;
const MONEY_RESPONSE_REGEX = /^\d+\.\d{2}$/;

export const PAGO_MODALIDAD = {
  NORMAL: 'NORMAL',
  ADELANTADO: 'ADELANTADO',
} as const;

export type PagoModalidad = (typeof PAGO_MODALIDAD)[keyof typeof PAGO_MODALIDAD];

export const FIFO_PREVIEW_POLICY = {
  OLDEST_PENDING_INVOICE_FIRST: 'FIFO_OLDEST_PENDING_INVOICE_FIRST',
  /**
   * The plan covers exactly the invoices the operator named, oldest first,
   * instead of every open invoice. In this mode the amount may not exceed the
   * selection, so no surplus is ever forecast as credit.
   */
  SELECTED_INVOICES_OLDEST_FIRST: 'PAYMENT_SELECTED_INVOICES_OLDEST_FIRST',
} as const;

export type FifoPreviewPolicy = (typeof FIFO_PREVIEW_POLICY)[keyof typeof FIFO_PREVIEW_POLICY];

const positiveMoneyInputSchema = z
  .string()
  .regex(MONEY_INPUT_REGEX, 'montoPago must be a valid money value (max 2 decimals)')
  .refine((value) => Number(value) > 0, 'montoPago must be greater than 0');

const responseMoneySchema = z
  .string()
  .regex(MONEY_RESPONSE_REGEX, 'money values must use 2 decimals');

export const PagoFifoPreviewRequestSchema = z.object({
  idCliente: z.coerce.number().int().positive(),
  montoPago: positiveMoneyInputSchema,
  modalidad: z.literal(PAGO_MODALIDAD.NORMAL),
  /**
   * Optional targeted selection: when present the forecast covers ONLY these
   * invoices (oldest first). Absent means the historical FIFO over every open
   * invoice. It is advisory input: the write path revalidates everything.
   */
  idFacturasPago: z
    .array(z.coerce.number().int().positive())
    .min(1, 'idFacturasPago must name at least one invoice')
    .optional(),
});

export type PagoFifoPreviewRequest = z.infer<typeof PagoFifoPreviewRequestSchema>;

export const PagoFifoPreviewInvoiceSchema = z.object({
  id: z.number().int().positive(),
  /**
   * Additive discriminator so the payment screen can list only open
   * EXTRAORDINARIA invoices for a PREPAGO client. Optional to stay
   * backward-compatible with payloads built before this field existed.
   */
  tipoFactura: z.enum([INVOICE_TYPE.PERIODICA, INVOICE_TYPE.EXTRAORDINARIA]).optional(),
  fechaInicio: z.string().min(1),
  fechaFin: z.string().min(1),
  gross: responseMoneySchema,
  saldoAntes: responseMoneySchema,
  montoAplicado: responseMoneySchema,
  saldoDespues: responseMoneySchema,
});

export type PagoFifoPreviewInvoice = z.infer<typeof PagoFifoPreviewInvoiceSchema>;

/**
 * An open invoice a LIVE prepaid package settles. It is reported apart from the
 * allocation plan so the operator can see why it cannot be paid with new money;
 * its period is covered by the package, and paying it would charge twice.
 */
export const PagoFifoPreviewCoveredInvoiceSchema = z.object({
  id: z.number().int().positive(),
  fechaInicio: z.string().min(1),
  fechaFin: z.string().min(1),
  saldoAntes: responseMoneySchema,
  idPaquete: z.number().int().positive(),
});

export type PagoFifoPreviewCoveredInvoice = z.infer<
  typeof PagoFifoPreviewCoveredInvoiceSchema
>;

export const PagoFifoPreviewResponseSchema = z.object({
  idCliente: z.number().int().positive(),
  modalidad: z.literal(PAGO_MODALIDAD.NORMAL),
  montoPago: responseMoneySchema,
  totalDeudaAntes: responseMoneySchema,
  totalAplicado: responseMoneySchema,
  creditoGenerado: responseMoneySchema,
  deudaDespues: responseMoneySchema,
  totalFacturasPendientes: z.number().int().nonnegative(),
  politica: z.enum([
    FIFO_PREVIEW_POLICY.OLDEST_PENDING_INVOICE_FIRST,
    FIFO_PREVIEW_POLICY.SELECTED_INVOICES_OLDEST_FIRST,
  ]),
  facturas: z.array(PagoFifoPreviewInvoiceSchema),
  /**
   * Open invoices a LIVE prepaid package settles: not payable with new money,
   * so they are reported apart from the plan together with the covering
   * package. Additive; absent in payloads built before this field existed.
   */
  facturasCubiertas: z.array(PagoFifoPreviewCoveredInvoiceSchema).optional(),
});

export type PagoFifoPreviewResponse = z.infer<typeof PagoFifoPreviewResponseSchema>;
